// Parses a season-ticket roster pasted into the admin or loaded from a CSV the
// team exported. This is the one input in the feature we don't control the
// shape of, so it tolerates what real exports actually contain: byte-order
// marks, \r\n line endings, quoted fields, a header row, and extra columns.
//
// The ACCOUNT NUMBER is the column that matters. A fan is verified against an
// identity Ticketmaster gives our server, and what that identity carries is an
// Archtics account number — so an account column is what actually grants a
// status. An email only grants when Ticketmaster returns one or the login
// token was signature-checked, neither of which is true today.
//
// Column mapping is by HEADER NAME when there is a header, because guessing is
// what went wrong before: taking "the first field that looks like an id" reads
// `John,Smith,john@x.test,6894461` as account "John" and silently throws the
// real account number away — a roster that then verifies nobody, for a reason
// no operator could see.

// "Acct ID", "acct_id", "Account No.", "Member #" — an Archtics export names
// this column a dozen ways, and a spelling we fail to recognise silently drops
// the roster back to guessing. The separator may be a space, an underscore, a
// dot or nothing at all.
const ACCOUNT_HEADER = /^(acct|account|member|customer)[\s._-]*(id|ids|no|nos|num|number|numbers|#)?\.?$/i;
const EMAIL_HEADER = /^(e-?mail)[\s._-]*(address|addr)?\.?$/i;

function splitFields(line) {
    return line.split(',').map(f => f.trim().replace(/^"(.*)"$/, '$1').trim());
}

// An account number is digits, or an alphanumeric id that ISN'T a word — a
// bare name like "John" must never be mistaken for one.
function looksLikeAccount(value) {
    if (!value) return false;
    if (/^\d+$/.test(value)) return true;
    // Archtics ids arrive whole sometimes: dsn.acct.suffix
    if (/^[A-Za-z0-9_-]+\.\d+\./.test(value)) return true;
    // Mixed letters AND digits (e.g. A12345) — a pure word is not an account.
    return /^[A-Za-z0-9_-]+$/.test(value) && /\d/.test(value);
}

// "cvarena.6894461.650741" → "6894461"; a bare number stands for itself.
function accountFromField(value) {
    const parts = String(value).split('.');
    return parts.length >= 3 ? parts[1] : String(value);
}

export function parseRosterRows(rosterText) {
    const rows = [];
    const lines = (rosterText || '').replace(/^﻿/, '').split(/\r?\n/);
    let accountIdx = -1;
    let emailIdx = -1;
    let headerSeen = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const fields = splitFields(line);

        // A header is the first non-empty line with no address in it that names
        // at least one column we understand.
        if (!headerSeen && rows.length === 0 && !line.includes('@')) {
            const accountAt = fields.findIndex(f => ACCOUNT_HEADER.test(f));
            const emailAt = fields.findIndex(f => EMAIL_HEADER.test(f));
            if (accountAt !== -1 || emailAt !== -1) {
                accountIdx = accountAt;
                emailIdx = emailAt;
                headerSeen = true;
                continue;
            }
            // Names a column but not one we map (e.g. "First,Last,Notes") —
            // still a header, still not data.
            if (/account|email|member|name/i.test(line)) { headerSeen = true; continue; }
        }

        // Named columns win outright; without them, fall back to shape.
        const email = (emailIdx !== -1 && fields[emailIdx] && fields[emailIdx].includes('@'))
            ? fields[emailIdx]
            : (fields.find(f => f.includes('@')) || '');
        let account = '';
        if (accountIdx !== -1) {
            account = fields[accountIdx] ? accountFromField(fields[accountIdx]) : '';
        } else if (headerSeen) {
            // A header that named its columns and did NOT name an account one
            // means there is no account column — not "go and guess". Guessing
            // here is what put ZIP codes and section numbers in the roster.
            account = '';
        } else {
            // No header at all. Only the documented shape is safe to read
            // positionally ("account,email" / "email,account"): in a wider row a
            // section number, ZIP or phone number is indistinguishable from an
            // account, and picking the wrong one discards the real account and
            // silently produces a roster that verifies nobody. An Archtics id is
            // unmistakable, so it is still accepted at any width.
            const dotted = fields.find(f => f !== email && /^[A-Za-z0-9_-]+\.\d+\./.test(f));
            if (dotted) {
                account = accountFromField(dotted);
            } else if (fields.filter(Boolean).length <= 2) {
                const candidate = fields.find(f => f !== email && looksLikeAccount(f));
                account = candidate ? accountFromField(candidate) : '';
            }
        }

        if (!account && !email) continue;
        rows.push({ account, email });
    }
    return rows;
}

// How many rows can actually verify a fan TODAY. Only an account number matches
// a server-verified identity, so this is the number worth putting in front of an
// operator — a roster of 5,000 emails and no accounts verifies nobody.
export function matchableCount(rows) {
    return rows.filter(row => row.account).length;
}

// Kept separate because an email is still worth uploading: it grants the moment
// Ticketmaster returns an address for a member, or JWKS is provisioned.
export function emailCount(rows) {
    return rows.filter(row => row.email).length;
}
